import { FC, Fragment, useContext, useEffect, useState } from 'react';
import { BookResponse } from 'ts/hotel/interfaces/BookResponse';
import { bookComplete } from 'services/hotel';
import { Screenshot, replaceHtml, showError } from 'utils/utils';
import { getCurrency, formatPriceCurrency } from 'utils/utils';
import { useNavigate } from 'react-router-dom';
import Button from 'components/ui/Button/Button';
import Rating from 'components/ui/Rating/Rating';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faArrowCircleLeft,
  faHotel,
  faList,
  faLocationDot,
  faTriangleExclamation,
  faUser,
} from '@fortawesome/free-solid-svg-icons';
import PassengerTable from './PassengerTable';
import { useTranslation } from 'react-i18next';
import BookSecret from './BookSecret';
import { formatPriceHotel, getCxlClass, getHotelDisplaySettings, getHotelIndex, getKey } from 'utils/hotel';
import { Price } from 'ts/hotel/interfaces/HotelDetails';
import BookLoadingModal from './BookLoadingModal';
import BookPayment from 'components/shared/BookPayment';
import { Credit2000PaymentInfo } from 'ts/shared/interfaces/Generic';
import SalesPrice from 'components/shared/SalesPrice';
import { dateFormat } from 'utils/shared/utils';
import withErrorBoundary from 'components/shared/withErrorBoundary';
import { getPriceModesList } from 'constants/hotel';
import AiNotes from './AiNotes';
import { MainContext } from 'contexts/MainContext';
import { HotelConfigContext } from 'contexts/HotelConfigContext';
import { b2b, needToScreenshot } from 'constants/generic';

interface BookPreviewProps {
  book: BookResponse;
  onPressBack: () => void;
  takeScreenshot: () => void;
  companyId: string;
  tripId: string;
  saleValue: string;
  specialRequestMessage: string;
  setSaleValue: (value: string) => void;
  recountTotalPrice: () => void;
  claudeRes?: any;
  aiNotesHidden: boolean;
}

const BookPreview: FC<BookPreviewProps> = ({ book, claudeRes, aiNotesHidden, ...props }) => {
  const [loading, setLoading] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(0);
  const navigate = useNavigate();
  const [t] = useTranslation();
  const { hotelIndex } = useContext(HotelConfigContext);
  const { user } = useContext(MainContext);
  const cxlclass = getCxlClass(book.roomClass.cxl.limitDate, !b2b);

  const { priceMode, currency } = getHotelDisplaySettings(hotelIndex);
  const netPriceModeLabel =
    (user && getPriceModesList(user).find((mode) => mode.value === priceMode)?.label) ||
    (priceMode === '1' ? 'Net' : 'Net.T');

  useEffect(() => {
    props.takeScreenshot();

    window.scrollTo(0, 0);
  }, []); // eslint-disable-line

  const submit = async (info?: Credit2000PaymentInfo | Credit2000PaymentInfo[], revolutCurrency?: string, revolut2?: boolean) => {
    setLoading(true);
    const screenshots = sessionStorage.getItem(getKey('screenshotsHotel', hotelIndex)) || '[]';
    const screenshotsParsed: Screenshot[] = JSON.parse(screenshots);

    const isMulti = Array.isArray(info);
    const response = await bookComplete({
      selectedRevolutCurrency: revolutCurrency,
      useLevel2Price: revolut2,
      orderId: book.orderId,
      paxes: book.paxes.map((pax) => ({ ...pax, citizenship: 'IL' })),
      creditCard: true,
      paymentSettings: isMulti ? undefined : info,
      multiPaymentSettings: isMulti ? info : undefined,
      screenshots: needToScreenshot ? screenshotsParsed.map((screen) => screen.data) : [],
      companyId: props.companyId,
      tripId: props.tripId,
      couponAmount: parseInt(props.saleValue || '0'),
      specialRequestMessage: props.specialRequestMessage,
      docket: book.docket,
      tourSubject: book.tourSubject,
      confirmPriceChange: book.cancellationPolicy.isPriceChange ? true : undefined,
    });

    setLoading(!!response?.bookBlocked);

    if (response?.paymentUrl && book.revolut) {
      window.location.href = response.paymentUrl;
    } else if (response && !response.hasErrors && !isMulti) {
      sessionStorage.setItem(
        getKey('bookPaymentHotels', hotelIndex),
        JSON.stringify({ ...response, paxes: book.paxes, couponAmount: props.saleValue })
      );
      navigate(`/hotel${getHotelIndex(hotelIndex)}/payment`);
    } else if (response && !response.hasErrors && isMulti) {
      sessionStorage.setItem(
        'bookPaymentsHotels',
        JSON.stringify({
          ...response,
          paxes: book.paxes,
          multiPaymentSettings: info,
          orderId: book.orderId,
        })
      );
      navigate('/hotel/multi-payment');
    } else {
      showError(response?.errorMessage || 'Error');
    }
  };

  const sendRequest = async () => {
    setLoading(true);
    const screenshots = sessionStorage.getItem(getKey('screenshotsHotel', hotelIndex)) || '[]';
    const screenshotsParsed: Screenshot[] = JSON.parse(screenshots);
    const response = await bookComplete({
      orderId: book.orderId,
      paxes: book.paxes.map((pax) => ({ ...pax, citizenship: 'IL' })),
      creditCard: false,
      paymentSettings: book.credit2000PaymentInfo,
      screenshots: needToScreenshot ? screenshotsParsed.map((screen) => screen.data) : [],
      companyId: props.companyId,
      tripId: props.tripId,
      couponAmount: parseInt(props.saleValue || '0'),
      specialRequestMessage: props.specialRequestMessage,
      docket: book.docket,
      tourSubject: book.tourSubject,
      confirmPriceChange: book.cancellationPolicy.isPriceChange ? true : undefined,
    });

    setLoading(!!response?.bookBlocked);

    if (!response?.hasErrors) {
      sessionStorage.setItem(
        getKey('bookSuccessHotels', hotelIndex),
        JSON.stringify({
          book: response?.data,
          paxes: book.paxes,
          orderId: book.orderId,
          orderHasBeenPaid: false,
          couponAmount: props.saleValue,
        })
      );
      navigate(`/hotel${getHotelIndex(hotelIndex)}/success`);
    } else {
      showError(response.errorMessage || 'Error');
    }
  };

  const getPaxName = (paxCode: number) => {
    const pax = book.paxes.find((item) => item.paxCode === paxCode)!;

    return `${pax.firstName} ${pax.lastName}`;
  };

  const onSuccessPassword = () => {
    if (showPasswordModal === 1) {
      submit(book.credit2000PaymentInfo);
    } else if (showPasswordModal === 2) {
      sendRequest();
    }

    setShowPasswordModal(0);
  };

  return (
    <div className="w100p">
      <div className="mb-20 default-shadow flex-between dyn-change-header">
        <Button
          title={t('back')}
          icon={faArrowCircleLeft}
          onClick={props.onPressBack}
          className="btn btn-link"
        />
      </div>
      <div className="book-preview default-shadow">
        <div className="title text-md title-header-grey">
          <FontAwesomeIcon size="sm" className="mr-5" icon={faHotel} />
          {t('hotel.details')}
        </div>
        <div className="book-preview-hotel">
          <div className="mb-10 text-md text-bold">{book.hotel.item.name}</div>
          <div className="text-light mb-10">
            <FontAwesomeIcon icon={faLocationDot} className="mr-5" />
            {book.hotel.addressInfo.address}
          </div>
          <Rating className="stars mb-10" value={book.hotel.item.star} />
          <div className="book-preview-hotel-rows">
            <div className="mb-10">
              {t('search.check_in')}:{' '}
              <span className="text-bold">
                {dateFormat(book.roomClass.checkIn, 'EEE - dd MMMM, yyyy')}
              </span>
            </div>
            <div className="mb-10">
              {t('search.check_out')}:{' '}
              <span className="text-bold">
                {dateFormat(book.roomClass.checkOut, 'EEE - dd MMMM, yyyy')}
              </span>
            </div>
            <div className="mb-10">
              {t('hotel.nights_up')}: <span className="text-bold">{book.roomClass.nights}</span>
            </div>
            <div className="mb-10">
              {t('passenger.room_count')}:{' '}
              <span className="text-bold">{book.roomClass.hotelRooms.length}</span>
            </div>
            <div className="">
              <span className="mr-5">{t('total')}:</span>

              <span className="text-bold text-lg mr-5">
                {formatPriceCurrency(
                  book.roomClass.price[currency as keyof Price],
                  getCurrency(currency),
                  true
                )}
                {props.saleValue && props.saleValue !== '0' && (
                  <span>
                    {' '}
                    (+{formatPriceCurrency(parseInt(props.saleValue || '0'), getCurrency(currency), true)}
                    )
                  </span>
                )}
              </span>
              {b2b &&
                book.roomClass.price.originalCurrency?.toLowerCase() !== currency.toLowerCase() && (
                  <span>
                    (
                    {formatPriceCurrency(
                      book.roomClass.price.originalValue,
                      getCurrency(book.roomClass.price.originalCurrency),
                      true
                    )}
                    )
                  </span>
                )}
            </div>

            {priceMode !== '0' && (
              <div className="">
                <span className="mr-5">{netPriceModeLabel}:</span>
                <span
                  className="text-bold text-lg mr-5"
                  dangerouslySetInnerHTML={{
                    __html: formatPriceHotel(book.roomClass.price, currency, priceMode, true),
                  }}
                />
              </div>
            )}
          </div>

        </div>
      </div>
      <div className="default-shadow book-preview">
        <div className="title text-md title-header-grey">
          <FontAwesomeIcon icon={faTriangleExclamation} className="mr-5" size={'sm'} />
          {t('passenger.cancellation_details')}
        </div>
        <div className={cxlclass.replace('block-mh', '') + ' mb-20'}>
          {new Date(book.roomClass.cxl.limitDate) < new Date()
            ? t('passenger.cancellation_outdate')
            : t('passenger.cancellation_date')}
          :{' '}
          <span className="text-bold">
            {dateFormat(book.cancellationPolicy.date, 'EEE - dd MMMM, yyyy')}
          </span>
          {b2b && (
            <div
              dangerouslySetInnerHTML={{ __html: replaceHtml(book.cancellationPolicy.desc) }}
            ></div>
          )}
        </div>
        {!!book.roomClass.remarks && (
          <div className="block-wr block-mh block-warning mb-20">
            <div className="title text-st mb-5">
              <FontAwesomeIcon icon={faTriangleExclamation} className="mr-5" />
              {t('passenger.important_notes')}
            </div>
            <div className="">{book.roomClass.remarks}</div>
          </div>
        )}

      </div>
      <AiNotes claudeRes={claudeRes} hidden={aiNotesHidden} prewiew={true} />
      <div className="default-shadow book-preview">
        <div className="title text-md title-header-grey">
          <FontAwesomeIcon icon={faList} className="mr-5" size={'sm'} />
          {t('passenger.order_details')}
        </div>

        <div className="title text-md">
          <FontAwesomeIcon icon={faUser} className="mr-5" size={'sm'} />
          {t('passenger.details')}
        </div>
        <div className="book-preview-table mb-20">
          <PassengerTable paxes={book.paxes} />
        </div>

        <div className="title text-md">
          <FontAwesomeIcon icon={faHotel} className="mr-5" size={'sm'} />
          {t('hotel.rooms')}
        </div>
        <div className="book-preview-table mb-20">
          <table>
            <thead>
              <tr>
                <th className="success-pax-longcol">{t('passenger.room')}</th>
                <th>{t('passenger.guests')}</th>
              </tr>
            </thead>
            <tbody>
              {book.roomClass.hotelRooms.map((room, roomIndex) => (
                <tr key={roomIndex}>
                  <td>
                    <div className="mb-5">
                      {room.desc + ' ' + book.roomClass.board.basis.desc}
                    </div>
                    {user?.branch?.hotelDisplaySupplierName && (
                      <span className="text-light">{book.hotel.hotelDetails?.sysSupp}</span>
                    )}
                  </td>
                  <td className="success-pax-multi">
                    {room.paxCodes.map((code, index) => (
                      <div key={index}>
                        {index + 1}. {getPaxName(code)}
                      </div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

          <Fragment>
            <SalesPrice
              currency={currency}
              price={book.roomClass.price[currency as keyof Price]}
              saleValue={props.saleValue}
              setSaleValue={props.setSaleValue}
              onClickApply={() => props.recountTotalPrice()}
            />

            <BookPayment
              onClickSend={sendRequest}
              onClickSubmit={submit}
              onClickMultiSubmit={submit}
              onClickRevolut={(cur) => submit(undefined, cur)}
              disabled={loading}
              privileges={{
                ...book.privilegesInfo,
                revolut: book.revolut
                  ? {
                    currency: book.revolutCurrency,
                    price: book.revolutPrice,
                    currencySecond: book.revolutCurrencySecond,
                    priceSecond: book.revolutPriceSecond,
                  }
                  : undefined,
                revolut2: book.revolutPriceLevel2
                  ? {
                    price: book.revolutPriceLevel2.revolutPrice,
                    priceSecond: book.revolutPriceLevel2.revolutPriceSecond,
                  }
                  : undefined,
              }}
              credit2000={book.credit2000PaymentInfo}
              multiCredit2000PaymentInfo={book.multiCredit2000PaymentInfo}
              systemProducts="Hotels"
              total={{
                value: book.roomClass.price[currency as keyof Price] + +props.saleValue,
                currency: currency,
              }}
              saleValue={{
                price: parseFloat(props.saleValue || '0'),
                currency: currency,
              }}
            />
          </Fragment>
        
      </div>
      {/* {b2b && book.privilegesInfo?.bookAllowed && (
          <div className="title-footer-grey">
            <Button
              title={'Confirm order'}
              className={'btn btn-primary btn-lg' + (loading ? ' btn-pulse' : '')}
              onClick={sendRequest}
              disabled={loading}
            />
          </div>
        )} */}

      {!!showPasswordModal && (
        <BookSecret onClose={() => setShowPasswordModal(0)} onSuccess={onSuccessPassword} />
      )}
      {loading && b2b && <BookLoadingModal book={book} />}
    </div>
  );
};

export default withErrorBoundary(BookPreview, 'BookPreview');
